/**
 * API service for communicating with backend
 */
import axios from 'axios';

// Microservices endpoints
const USER_SERVICE_URL = process.env.REACT_APP_USER_SERVICE_URL || 'http://localhost:8001';
const WORKFLOW_SERVICE_URL = process.env.REACT_APP_WORKFLOW_SERVICE_URL || 'http://localhost:8002';
const ORCHESTRATION_SERVICE_URL = process.env.REACT_APP_ORCHESTRATION_SERVICE_URL || 'http://localhost:8003';

// Create axios instance for user service
const userServiceApi = axios.create({
  baseURL: USER_SERVICE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create axios instance for workflow service
const workflowServiceApi = axios.create({
  baseURL: WORKFLOW_SERVICE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to all requests
const addAuthInterceptor = (apiInstance) => {
  apiInstance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

addAuthInterceptor(userServiceApi);
addAuthInterceptor(workflowServiceApi);

// Legacy api instance for backward compatibility (points to user service)
const api = userServiceApi;

// Auth API
export const authAPI = {
  login: async (email, password) => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);

    const response = await api.post('/auth/token', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  register: async (userData) => {
    const response = await api.post('/users/', userData);
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  googleAuth: async (credential) => {
    const response = await api.post('/auth/google', { credential });
    return response.data;
  },
};

// User API
export const userAPI = {
  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  updateProfile: async (userId, profileData) => {
    const response = await api.put(`/users/${userId}`, profileData);
    return response.data;
  },

  changePassword: async (passwordData) => {
    const response = await api.post('/users/change-password', passwordData);
    return response.data;
  },

  enable2FA: async () => {
    const response = await api.post('/users/2fa/enable');
    return response.data;
  },

  disable2FA: async (code) => {
    const response = await api.post('/users/2fa/disable', { code });
    return response.data;
  },

  uploadAvatar: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/users/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// Workflow API (uses workflow service on port 8002)
export const workflowAPI = {
  listWorkflows: async () => {
    const response = await workflowServiceApi.get('/workflows/');
    return response.data;
  },

  getWorkflow: async (workflowId) => {
    const response = await workflowServiceApi.get(`/workflows/${workflowId}`);
    return response.data;
  },

  createWorkflow: async (workflowData) => {
    const response = await workflowServiceApi.post('/workflows/', workflowData);
    return response.data;
  },

  updateWorkflow: async (workflowId, workflowData) => {
    const response = await workflowServiceApi.put(`/workflows/${workflowId}`, workflowData);
    return response.data;
  },

  deleteWorkflow: async (workflowId) => {
    await workflowServiceApi.delete(`/workflows/${workflowId}`);
  },

  executeWorkflow: async (workflowId, inputData) => {
    const response = await workflowServiceApi.post(`/workflows/${workflowId}/execute`, inputData);
    return response.data;
  },

  listExecutions: async (workflowId) => {
    const response = await workflowServiceApi.get(`/workflows/${workflowId}/executions`);
    return response.data;
  },

  getExecution: async (executionId) => {
    const response = await workflowServiceApi.get(`/workflows/executions/${executionId}`);
    return response.data;
  },
};

// Template API (uses workflow service on port 8002)
export const templateAPI = {
  listTemplates: async (category = null) => {
    const params = category ? { category } : {};
    const response = await workflowServiceApi.get('/templates/', { params });
    return response.data;
  },

  getTemplate: async (templateId) => {
    const response = await workflowServiceApi.get(`/templates/${templateId}`);
    return response.data;
  },
};

export default api;
